// Single source of truth for projects.
// `description` and `details` carry both languages: { en, fr }.
// Order is preserved for rendering; no external index mapping.
export const projects = [
    {
        title: "JobHunter AI",
        technologies: ["Python", "FastAPI", "Next.js", "React", "Google Gemini"],
        image: null,
        link: "https://github.com/hediipor/JobHunter",
        github: "https://github.com/hediipor/JobHunter",
        demo: null,
        description: {
            en: "AI-powered job and internship search assistant that scrapes, matches, and tailors applications automatically",
            fr: "Assistant de recherche d'emploi et de stage propulsé par l'IA qui scrape, met en correspondance et personnalise les candidatures automatiquement",
        },
        details: {
            en: `An end-to-end job hunting assistant that automates the repetitive parts of applying:

• Automated Scraping: Scheduled scans of job and internship boards with a matching engine that ranks new openings by fit
• Email Alerts: Notifications for fresh opportunities matched to the user's profile
• AI Tailoring: Google Gemini API generates a tailored resume and cover letter per posting
• PDF Rendering: Generated documents are rendered to clean, ATS-friendly PDFs with ReportLab
• Matching Engine: Keyword and profile-based scoring to filter noise from real matches

Technical Architecture:
- Backend: Python with FastAPI for the API and background scan scheduling
- Frontend: Next.js / React dashboard for reviewing matches and generated documents
- AI Layer: Google Gemini API for resume and cover-letter generation
- Document Pipeline: ReportLab for programmatic PDF generation`,
            fr: `Un assistant de recherche d'emploi de bout en bout qui automatise les tâches répétitives des candidatures :

• Scraping automatisé : Analyses planifiées des offres d'emploi et de stage avec un moteur de correspondance qui classe les nouvelles offres par pertinence
• Alertes e-mail : Notifications pour les nouvelles opportunités correspondant au profil de l'utilisateur
• Personnalisation IA : L'API Google Gemini génère un CV et une lettre de motivation adaptés à chaque offre
• Génération PDF : Les documents sont rendus en PDF propres et compatibles ATS avec ReportLab
• Moteur de correspondance : Scoring basé sur les mots-clés et le profil pour filtrer le bruit

Architecture technique :
- Backend : Python avec FastAPI pour l'API et la planification des analyses en arrière-plan
- Frontend : Tableau de bord Next.js / React pour consulter les correspondances et les documents générés
- Couche IA : API Google Gemini pour la génération de CV et lettres de motivation
- Pipeline documentaire : ReportLab pour la génération programmatique de PDF`,
        },
    },
    {
        title: "Cafe Management System",
        technologies: ["FastAPI", "WebSockets", "React", "Python"],
        image: null,
        link: "https://github.com/hediipor/cafe-management-demo",
        github: "https://github.com/hediipor/cafe-management-demo",
        demo: null,
        description: {
            en: "Real-time cafe demo that keeps a register and waiter phones in sync as orders and tables change",
            fr: "Démo de café en temps réel qui synchronise une caisse et les téléphones des serveurs au fil des commandes et des tables",
        },
        details: {
            en: `A live demo of a connected cafe floor, built for a pitch:

• Real-time Sync: A FastAPI + WebSocket backend broadcasts live table and order updates to every connected device instantly
• Role-based Access: JWT authentication with permissions enforced server-side (waiter vs. admin)
• Register View: Admin-facing screen for managing tables, orders, and billing
• Waiter View: Mobile-friendly screen for taking and updating orders from the floor
• Instant Updates: No manual refresh — every device reflects the current floor state

Technical Implementation:
- Backend: FastAPI with native WebSocket endpoints for the broadcast layer
- Frontend: React clients for the register and the waiter phones
- Auth: JWT tokens with role-based permission checks on every action
- State: Single source of truth on the server, pushed to clients on change`,
            fr: `Une démo en direct d'une salle de café connectée, conçue pour un pitch :

• Synchronisation en temps réel : Un backend FastAPI + WebSocket diffuse instantanément les mises à jour des tables et des commandes à chaque appareil connecté
• Accès basé sur les rôles : Authentification JWT avec permissions appliquées côté serveur (serveur vs. administrateur)
• Vue caisse : Écran administrateur pour gérer les tables, les commandes et la facturation
• Vue serveur : Écran adapté au mobile pour prendre et mettre à jour les commandes en salle
• Mises à jour instantanées : Aucun rafraîchissement manuel — chaque appareil reflète l'état actuel de la salle

Implémentation technique :
- Backend : FastAPI avec des endpoints WebSocket natifs pour la couche de diffusion
- Frontend : Clients React pour la caisse et les téléphones des serveurs
- Authentification : Jetons JWT avec vérification des permissions par rôle à chaque action
- État : Source de vérité unique sur le serveur, poussée vers les clients à chaque changement`,
        },
    },
    {
        title: "Personal Portfolio Website",
        technologies: ["React", "Tailwind CSS", "JavaScript", "Google Gemini", "Vercel"],
        image: null,
        link: "https://github.com/hediipor",
        github: "https://github.com/hediipor",
        demo: null,
        description: {
            en: "Responsive single-page portfolio with a bilingual UI, dark/light mode, and an AI chatbot",
            fr: "Portfolio single-page responsive avec interface bilingue, mode sombre/clair et un chatbot IA",
        },
        details: {
            en: `This site — a single-page portfolio focused on motion, accessibility, and a native-feeling UI:

• Animated UI: Framer Motion transitions and an interactive skills visualization
• Theme Toggle: Full dark/light mode with system preference detection
• Bilingual: English / French content driven from a single translation layer
• AI Chatbot: Answers questions about my background, powered by the Google Gemini API through a serverless proxy so the key stays server-side
• Deployment: Deployed on Vercel with a custom domain

Technical Implementation:
- Frontend: React with a component-driven architecture and Tailwind CSS
- Animation: Framer Motion for scroll and interaction animations
- Content: Single source of truth for projects, experience, and skills
- Backend: Vercel serverless function proxying chat requests to Gemini`,
            fr: `Ce site — un portfolio single-page axé sur le mouvement, l'accessibilité et une interface au rendu natif :

• Interface animée : Transitions Framer Motion et visualisation interactive des compétences
• Bascule de thème : Mode sombre/clair complet avec détection de la préférence système
• Bilingue : Contenu anglais / français piloté depuis une seule couche de traduction
• Chatbot IA : Répond aux questions sur mon parcours, propulsé par l'API Google Gemini via un proxy serverless pour garder la clé côté serveur
• Déploiement : Déployé sur Vercel avec un domaine personnalisé

Implémentation technique :
- Frontend : React avec une architecture orientée composants et Tailwind CSS
- Animation : Framer Motion pour les animations de défilement et d'interaction
- Contenu : Source de vérité unique pour les projets, l'expérience et les compétences
- Backend : Fonction serverless Vercel relayant les requêtes de chat vers Gemini`,
        },
    },
    {
        title: "Mentorny - Mentor-Mentee Platform",
        technologies: ["Angular", "TypeScript", "Node.js", "Express", "MongoDB"],
        image: null,
        link: "https://github.com/hediipor/Mentorny",
        github: "https://github.com/hediipor/Mentorny",
        demo: null,
        description: {
            en: "Full-stack MEAN stack web application connecting mentors and mentees with real-time messaging and session management",
            fr: "Application web full-stack MEAN connectant mentors et mentorés avec messagerie en temps réel et gestion de sessions",
        },
        details: {
            en: `Enterprise-grade mentorship platform developed during professional internship at Rhibi Consulting:

• User Management: Comprehensive authentication system with role-based access control
• Real-time Messaging: WebSocket-based chat functionality for mentor-mentee communication
• Session Scheduling: Calendar integration for booking and managing mentorship sessions
• Profile System: Detailed user profiles with skills, availability, and mentorship preferences
• Matching Algorithm: Smart mentor-mentee pairing based on skills and interests
• Request Management: Workflow for mentorship requests, approvals, and tracking

Technical Architecture:
- Frontend: Angular 13+ with TypeScript for type-safe development
- Backend: Node.js with Express.js RESTful API architecture
- Database: MongoDB with Mongoose ODM for flexible data modeling
- Authentication: JWT-based authentication with secure token management
- Real-time: Socket.io integration for instant messaging

Security & Best Practices:
- Route protection with Angular guards
- Password hashing with bcrypt
- Input validation and sanitization
- CORS configuration for secure cross-origin requests
- Environment-based configuration management`,
            fr: `Plateforme de mentorat de niveau professionnel développée lors d'un stage chez Rhibi Consulting :

• Gestion des utilisateurs : Système d'authentification complet avec contrôle d'accès basé sur les rôles
• Messagerie en temps réel : Fonctionnalité de chat via WebSocket pour la communication mentor-mentoré
• Planification de sessions : Intégration calendrier pour réserver et gérer les sessions de mentorat
• Système de profils : Profils détaillés avec compétences, disponibilités et préférences de mentorat
• Algorithme de correspondance : Jumelage intelligent mentor-mentoré basé sur les compétences et intérêts
• Gestion des demandes : Flux de travail pour les demandes de mentorat, approbations et suivi

Architecture technique :
- Frontend : Angular 13+ avec TypeScript pour un développement typé
- Backend : Node.js avec architecture RESTful Express.js
- Base de données : MongoDB avec Mongoose ODM pour une modélisation flexible
- Authentification : JWT avec gestion sécurisée des tokens
- Temps réel : Intégration Socket.io pour la messagerie instantanée

Sécurité et bonnes pratiques :
- Protection des routes avec Angular guards
- Hachage de mots de passe avec bcrypt
- Validation et assainissement des entrées
- Configuration CORS pour les requêtes cross-origin sécurisées`,
        },
    },
    {
        title: "To Do APP",
        technologies: ["React", "JavaScript", "HTML5", "CSS3"],
        image: null,
        link: "https://github.com/hediipor/ToDo_APP",
        github: "https://github.com/hediipor/ToDo_APP",
        demo: null,
        description: {
            en: "Full-stack task management web application with persistent storage and real-time updates",
            fr: "Application web full-stack de gestion de tâches avec stockage persistant et mises à jour en temps réel",
        },
        details: {
            en: `A comprehensive task management solution built with React, featuring:

• CRUD Operations: Create, read, update, and delete tasks with intuitive UI
• Persistent Storage: LocalStorage integration for data persistence across sessions
• Responsive Design: Mobile-first approach ensuring seamless experience on all devices
• State Management: Efficient React hooks implementation for optimal performance
• Clean Architecture: Component-based structure following React best practices

Technical Highlights:
- Implemented custom hooks for localStorage synchronization
- Optimized re-renders using React.memo and useCallback
- Accessible UI following WCAG guidelines`,
            fr: `Solution complète de gestion de tâches construite avec React :

• Opérations CRUD : Créer, lire, mettre à jour et supprimer des tâches via une interface intuitive
• Stockage persistant : Intégration LocalStorage pour la persistance des données entre sessions
• Design responsive : Approche mobile-first pour une expérience fluide sur tous les appareils
• Gestion d'état : Implémentation efficace des hooks React pour des performances optimales
• Architecture propre : Structure orientée composants suivant les meilleures pratiques React

Points techniques :
- Hooks personnalisés pour la synchronisation avec localStorage
- Re-rendus optimisés avec React.memo et useCallback
- Interface accessible suivant les directives WCAG`,
        },
    },
    {
        title: "Currency Converter Mobile App",
        technologies: ["Flutter", "Dart"],
        image: null,
        link: "https://github.com/hediipor/Flutter-Currency-Converter-APP",
        github: "https://github.com/hediipor/Flutter-Currency-Converter-APP",
        demo: null,
        description: {
            en: "Cross-platform mobile application for real-time currency conversion with offline support",
            fr: "Application mobile cross-platform de conversion de devises en temps réel avec support hors ligne",
        },
        details: {
            en: `Native-quality mobile application developed with Flutter framework:

• Real-time Conversion: USD to TND currency conversion with live exchange rates
• Offline Capability: Cached exchange rates for offline functionality
• Material Design: Modern UI following Flutter's Material Design principles
• Cross-Platform: Single codebase deployed on both iOS and Android
• Error Handling: Robust error management for network failures

Technical Implementation:
- HTTP client integration for API calls
- State management using Provider pattern
- Custom widgets for reusable UI components
- Platform-specific optimizations for performance`,
            fr: `Application mobile de qualité native développée avec Flutter :

• Conversion en temps réel : Conversion USD vers TND avec taux de change en direct
• Mode hors ligne : Taux de change mis en cache pour fonctionner sans connexion
• Material Design : Interface moderne suivant les principes Material Design de Flutter
• Multi-plateforme : Base de code unique déployée sur iOS et Android
• Gestion des erreurs : Gestion robuste des pannes réseau

Implémentation technique :
- Client HTTP pour les appels API
- Gestion d'état avec le pattern Provider
- Widgets personnalisés pour les composants UI réutilisables
- Optimisations spécifiques à chaque plateforme`,
        },
    },
    {
        title: "Weather Mobile APP",
        technologies: ["Flutter", "Dart"],
        image: null,
        link: "https://github.com/hediipor/weather_app",
        github: "https://github.com/hediipor/weather_app",
        demo: null,
        description: {
            en: "Feature-rich weather forecasting application with geolocation and multi-day predictions",
            fr: "Application météo complète avec géolocalisation et prévisions multi-jours",
        },
        details: {
            en: `Advanced weather application leveraging external APIs and device sensors:

• Weather API Integration: Real-time weather data from OpenWeatherMap API
• Geolocation Services: Automatic location detection using device GPS
• Multi-day Forecast: 7-day weather predictions with hourly breakdowns
• Custom Animations: Smooth transitions and weather-specific animations
• Dynamic Theming: UI adapts based on weather conditions

Technical Features:
- RESTful API integration with error handling
- JSON parsing and data serialization
- Custom icon sets and Lottie animations
- Location services with permission handling
- Responsive layouts for various screen sizes`,
            fr: `Application météo avancée exploitant des API externes et les capteurs de l'appareil :

• Intégration API météo : Données météo en temps réel depuis OpenWeatherMap
• Géolocalisation : Détection automatique de la position via GPS
• Prévisions multi-jours : Prévisions sur 7 jours avec détails horaires
• Animations personnalisées : Transitions fluides et animations thématiques
• Thème dynamique : L'interface s'adapte aux conditions météorologiques

Fonctionnalités techniques :
- Intégration RESTful avec gestion des erreurs
- Parsing JSON et sérialisation des données
- Jeux d'icônes personnalisés et animations Lottie
- Services de localisation avec gestion des permissions`,
        },
    },
    {
        title: "AI Taquin Game Solver",
        technologies: ["Python"],
        image: null,
        link: "https://github.com/hediipor/Python_AI_Taquin_Game",
        github: "https://github.com/hediipor/Python_AI_Taquin_Game",
        demo: null,
        description: {
            en: "Intelligent puzzle solver using AI algorithms with step-by-step visualization",
            fr: "Solveur de puzzle intelligent utilisant des algorithmes IA avec visualisation étape par étape",
        },
        details: {
            en: `AI-powered sliding puzzle solver implementing advanced search algorithms:

• AI Algorithms: A* and BFS pathfinding for optimal solution discovery
• Interactive GUI: Tkinter-based interface for puzzle visualization
• Step Visualization: Animated solution playback showing each move
• Heuristic Functions: Manhattan distance and misplaced tiles heuristics
• Performance Metrics: Solution time and move count tracking

Technical Implementation:
- Priority queue for A* algorithm optimization
- State space search with visited state tracking
- GUI event handling and animation system
- Algorithm comparison and benchmarking
- Memory-efficient state representation`,
            fr: `Solveur de taquin propulsé par l'IA implémentant des algorithmes de recherche avancés :

• Algorithmes IA : Algorithmes A* et BFS pour trouver la solution optimale
• Interface graphique : Interface Tkinter pour la visualisation du puzzle
• Visualisation pas à pas : Lecture animée de la solution avec chaque mouvement
• Fonctions heuristiques : Distance de Manhattan et heuristique des tuiles mal placées
• Métriques de performance : Suivi du temps de résolution et du nombre de mouvements

Implémentation technique :
- File de priorité pour l'optimisation de l'algorithme A*
- Recherche dans l'espace d'états avec suivi des états visités
- Gestion des événements GUI et système d'animation
- Comparaison et benchmarking des algorithmes`,
        },
    },
    {
        title: "Hotel Website",
        technologies: ["HTML5", "CSS3", "JavaScript"],
        image: null,
        link: "https://github.com/hediipor/Hotel_website",
        github: "https://github.com/hediipor/Hotel_website",
        demo: null,
        description: {
            en: "Modern, responsive hotel booking platform with interactive gallery and contact system",
            fr: "Plateforme de réservation hôtelière moderne et responsive avec galerie interactive",
        },
        details: {
            en: `Professional hotel website with modern web technologies:

• Responsive Design: Mobile-first approach with CSS Grid and Flexbox
• Interactive Gallery: Image carousel with lightbox functionality
• Booking System: Form validation and user input handling
• Contact Integration: Email integration for customer inquiries
• SEO Optimized: Semantic HTML and meta tags for search visibility

Technical Features:
- Vanilla JavaScript for DOM manipulation
- CSS animations and transitions
- Form validation with regex patterns
- Lazy loading for image optimization
- Cross-browser compatibility testing`,
            fr: `Site web hôtelier professionnel avec technologies web modernes :

• Design responsive : Approche mobile-first avec CSS Grid et Flexbox
• Galerie interactive : Carrousel d'images avec fonctionnalité lightbox
• Système de réservation : Validation de formulaires et saisie utilisateur
• Intégration contact : Intégration e-mail pour les demandes clients
• SEO optimisé : HTML sémantique et balises meta pour la visibilité

Fonctionnalités techniques :
- JavaScript vanilla pour la manipulation du DOM
- Animations et transitions CSS
- Validation de formulaires avec expressions régulières
- Chargement paresseux pour l'optimisation des images`,
        },
    },
    {
        title: "Weekly Budget Tracker PWA",
        technologies: ["HTML5", "CSS3", "JavaScript"],
        image: null,
        link: "https://github.com/hediipor/expense-tracker",
        github: "https://github.com/hediipor/expense-tracker",
        demo: "https://hedi-budget-tracker.vercel.app",
        description: {
            en: "Progressive Web App for weekly expense tracking with offline support and budget management",
            fr: "Application Web Progressive de suivi des dépenses hebdomadaires avec support hors ligne",
        },
        details: {
            en: `Full-featured Progressive Web App for personal finance management:

• Budget Management: Set weekly budget goals with automatic rollover
• Transaction Tracking: Log expenses and income with category classification
• Real-time Updates: Live budget calculations and remaining balance display
• Offline Functionality: Service Worker implementation for offline access
• Data Persistence: LocalStorage integration for cross-session data retention
• PWA Features: Installable on mobile devices as native-like app

Technical Implementation:
- Service Worker for offline caching and PWA functionality
- LocalStorage API for client-side data persistence
- Responsive design optimized for mobile and desktop
- Manifest.json for PWA installation capabilities
- Transaction categorization system (Food, Transport, Shopping, etc.)`,
            fr: `Application Web Progressive complète pour la gestion des finances personnelles :

• Gestion du budget : Objectifs hebdomadaires avec report automatique
• Suivi des transactions : Enregistrement des dépenses et revenus par catégorie
• Mises à jour en temps réel : Calculs du budget et solde restant en direct
• Mode hors ligne : Service Worker pour l'accès hors connexion
• Persistance des données : LocalStorage pour la conservation entre sessions
• Fonctionnalités PWA : Installable sur mobile comme une app native

Implémentation :
- Service Worker pour la mise en cache et les fonctionnalités PWA
- API LocalStorage pour la persistance côté client
- Design responsive optimisé mobile et bureau
- Système de catégorisation (Alimentation, Transport, Shopping, etc.)`,
        },
    },
    {
        title: "Jeu Devinette (Guessing Game)",
        technologies: ["C"],
        image: null,
        link: "https://github.com/hediipor/jeu_devinette",
        github: "https://github.com/hediipor/jeu_devinette",
        demo: null,
        description: {
            en: "Interactive number guessing game built in C with score management and file persistence",
            fr: "Jeu de devinettes interactif développé en C avec gestion des scores et persistance des données",
        },
        details: {
            en: `Console-based guessing game demonstrating core programming concepts in C:

• Game Mechanics: Random number generation with user input validation
• Hint System: Dynamic feedback based on player guesses (higher/lower)
• Attempt Tracking: Count and display number of attempts per game
• Score Management: Sorted linked list implementation for high scores
• File Persistence: Save and load scores from scores.txt file

Technical Features:
- Custom linked list data structure for score management
- File I/O operations for persistent storage
- Random number generation using stdlib
- Input validation and error handling
- Memory management with dynamic allocation
- Struct-based data modeling (Joueur, Node)`,
            fr: `Jeu console démontrant les concepts fondamentaux de la programmation en C :

• Mécaniques de jeu : Génération de nombres aléatoires avec validation des entrées
• Système d'indices : Retour dynamique selon les suppositions (plus haut/plus bas)
• Suivi des tentatives : Comptage et affichage du nombre de tentatives par partie
• Gestion des scores : Implémentation d'une liste chaînée triée pour les meilleurs scores
• Persistance : Sauvegarde et chargement des scores depuis scores.txt

Fonctionnalités techniques :
- Structure de données liste chaînée personnalisée
- Opérations d'entrée/sortie sur fichiers
- Génération de nombres aléatoires avec stdlib
- Validation des entrées et gestion des erreurs
- Gestion de la mémoire avec allocation dynamique
- Modélisation par structures (Joueur, Node)`,
        },
    },
];
